import {Component, ElementRef, HostBinding, Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {ProductionScheduleBuilderAspectRatio} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-builder-aspect-ratio';
import {EditProductionScheduleItemInPathGroupAction} from '@presentation/pages/texfab/production-schedule/add/plan/modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group-action';
import {EditProductionScheduleItemInPathGroupComponent} from '@presentation/pages/texfab/production-schedule/add/plan/modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {AssertionUtils, DialogBuilderFactoryService, Unit} from '@vdw/angular-component-library';
import {select as d3Select} from 'd3-selection';
import {isNil} from 'lodash-es';
import {filter} from 'rxjs/operators';
import {ProductionScheduleItemInPathGroupRepeatIndicatorNewComponent} from '../production-schedule-item-in-path-group-repeat-indicator-new/production-schedule-item-in-path-group-repeat-indicator-new.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-production-schedule-item-in-path-group-container-new]',
  templateUrl: './production-schedule-item-in-path-group-container-new.component.html',
  styleUrls: ['./production-schedule-item-in-path-group-container-new.component.scss']
})
export class ProductionScheduleItemInPathGroupContainerNewComponent implements OnInit, OnChanges {
  @Input() public productionScheduleItem: ProductionScheduleItemInPathGroup;
  @Input() public productionScheduleItemIndexInPath: number;
  @Input() public productionScheduleBuilderHeight: number;
  @Input() public commercialUnit: Unit;
  @Input() public aspectRatio: ProductionScheduleBuilderAspectRatio;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionSchedulePathIndex: number;
  @Input() public productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public isProductionScheduleDisplayOverview: boolean;
  @HostBinding('class') public readonly labelForProductionScheduleItem = 'production-schedule-item';
  @HostBinding('attr.y') public verticalPosition: number;
  @HostBinding('attr.height') public totalHeight: number;

  public cutbarBeforeHeight: number;
  public cutbarAfterHeight: number;
  public drawingImageHeight: number;
  public drawingHeight: number;
  public drawingWidth: number;

  private readonly MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX = 1;

  public constructor(
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    public readonly elementRef: ElementRef
  ) {}

  public ngOnInit(): void {
    d3Select(this.elementRef.nativeElement).datum(this.productionScheduleItem);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('aspectRatio' in changes || 'productionSchedule' in changes || 'productionScheduleBuilderHeight' in changes) {
      this.setHeightDimensionsForOverviewViewMode();

      this.drawingWidth = isNaN(this.aspectRatio.width) ? 0 : this.productionScheduleItem.technicalDimensions.widthInDents * this.aspectRatio.width;

      this.setVerticalPosition();
    }
  }

  public openEditProductionScheduleItemDialog(event: MouseEvent): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .withWidth('334px')
      .withClass('edit-drawing-in-path')
      .openDialog(EditProductionScheduleItemInPathGroupComponent, {
        productionScheduleItemInPathGroup: this.productionScheduleItem,
        commercialUnit: this.commercialUnit,
        productionSchedule: this.productionSchedule,
        matDialogPosition: {
          y: event.clientY,
          x: event.clientX
        },
        allowExceedingRequestedAmountOfOrderLineItems: this.productionOrderCustomSettings?.allowExceedingRequestedAmountOfOrderLineItems,
        pickDensityInPicksPerMeter: this.productionSchedule.machineQuality.pickDensity * 1000
      })
      .pipe(filter((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => !isNil(result)))
      .subscribe((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => {
        if (result.action === EditProductionScheduleItemInPathGroupAction.EDIT) {
          this.editProductionScheduleItem(result.value);
        } else {
          this.removeProductionScheduleItemInPathGroup();
        }
      });
  }

  public canShowRepeatIndicator(): boolean {
    return this.productionScheduleItem.quantity > 1;
  }

  public static getCutbarHeight(
    cutbar: {commercialLengthInMM: number; technicalLengthInNumberOfPicks: number},
    minimumHeight: number,
    aspectRatioHeight: number
  ): {cutbarHeight: number; offsetHeight: number} {
    if (!cutbar.commercialLengthInMM || !cutbar.technicalLengthInNumberOfPicks) {
      return {cutbarHeight: 0, offsetHeight: 0};
    } else {
      const proportionalCutbarHeight = cutbar.technicalLengthInNumberOfPicks * aspectRatioHeight;
      return {cutbarHeight: Math.max(proportionalCutbarHeight, minimumHeight), offsetHeight: Math.max(0, minimumHeight - proportionalCutbarHeight)};
    }
  }

  private setHeightDimensionsForOverviewViewMode(): void {
    let offsetHeight = 0;
    if (!AssertionUtils.isNullOrUndefined(this.productionScheduleItem.getCutbarInformation())) {
      const {cutbarBefore, cutbarAfter} = this.productionScheduleItem.getCutbarInformation();

      const cutbarBeforeHeightInfo = ProductionScheduleItemInPathGroupContainerNewComponent.getCutbarHeight(cutbarBefore, this.MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX, this.aspectRatio.height);
      const cutbarAfterHeightInfo = ProductionScheduleItemInPathGroupContainerNewComponent.getCutbarHeight(cutbarAfter, this.MINIMUM_HEIGHT_FOR_CUTBAR_IN_PX, this.aspectRatio.height);

      this.cutbarBeforeHeight = cutbarBeforeHeightInfo.cutbarHeight;
      this.cutbarAfterHeight = cutbarAfterHeightInfo.cutbarHeight;
      offsetHeight = cutbarBeforeHeightInfo.offsetHeight + cutbarAfterHeightInfo.offsetHeight;
    }
    this.drawingHeight = this.productionScheduleItem.technicalDimensions.heightInPicks * this.aspectRatio.height - offsetHeight;
    this.drawingImageHeight = this.drawingHeight;
    this.totalHeight = (this.cutbarAfterHeight + this.cutbarBeforeHeight + this.drawingHeight) * this.productionScheduleItem.quantity;

    let requiredHeightForSingleProductionScheduleItemRepetition = this.cutbarAfterHeight + this.cutbarBeforeHeight + this.drawingHeight;

    if (this.productionScheduleItem.quantity > 1) {
      requiredHeightForSingleProductionScheduleItemRepetition += ProductionScheduleItemInPathGroupRepeatIndicatorNewComponent.minimumAllowedHeight;
    }

    if (requiredHeightForSingleProductionScheduleItemRepetition > this.totalHeight) {
      this.drawingImageHeight = Math.max(0, this.drawingImageHeight - (requiredHeightForSingleProductionScheduleItemRepetition - this.totalHeight));
      this.drawingHeight = Math.max(0, this.drawingHeight - (requiredHeightForSingleProductionScheduleItemRepetition - this.totalHeight));
    }
  }

  private editProductionScheduleItem(productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup): void {
    this.productionScheduleItem = productionScheduleItemInPathGroup;

    this.productionSchedulePlan.editProductionScheduleItem(
      productionScheduleItemInPathGroup,
      this.productionScheduleItemIndexInPath,
      this.productionSchedulePathIndex,
      this.productionScheduleColoredYarnSet.startDent
    );
  }

  private removeProductionScheduleItemInPathGroup(): void {
    const productionSchedulePathOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(this.productionScheduleColoredYarnSet.startDent);
    const productionSchedulePath = productionSchedulePathOfColoredYarnSet.productionSchedulePaths[this.productionSchedulePathIndex];

    this.productionSchedulePlan.removeProductionScheduleItem(
      this.productionScheduleItem.uuid,
      productionSchedulePath.pathNumber,
      productionSchedulePathOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );
  }

  private setVerticalPosition(): void {
    this.verticalPosition =
      !isNil(this.elementRef.nativeElement) && this.productionScheduleItemIndexInPath !== 0
        ? this.elementRef.nativeElement.previousElementSibling.getAttribute('y') - this.totalHeight
        : this.productionScheduleBuilderHeight - this.totalHeight;
  }
}
